import {
  isLegacyStockProductData,
  Status,
  StockDto,
  TLegacyStock,
} from '@services/MarppleShop/features/ProductList';
import { TProductBadgeViewProps } from '@services/MarppleShop/renderApp/components/cells/ProductBadge/ProductBadgeView.ts';
import { ProductTimeHelper } from '@services/MarppleShop/renderApp/components/cells/ProductCard/utils/ProductTimeHelper.ts';
import { differenceInHours, differenceInMinutes } from 'date-fns';

export type BadgeParams =
  | {
      goodsTypeId: 'MPS' | 'DTG';
      status: Partial<Status>;
      legacyStock: TLegacyStock;
      salePeriod: {
        startDate?: Date;
        endDate?: Date;
      };
      outOfStock: boolean;
      publicAt?: Date;
    }
  | {
      goodsTypeId: 'PBG' | 'TPL';
      status: Partial<Status>;
      stock: StockDto;
      salePeriod: {
        startDate?: Date;
        endDate?: Date;
      };
      outOfStock: boolean;
      publicAt?: Date;

      // 한정수량 메타데이터
      limitedMeta?: {
        isLimited: boolean;
      };

      // 품절임박 메타데이터
      lowStockMeta?: {
        threshold: number;
      };
    };

const createBadges = (props: BadgeParams, baseTimestamp: number) => {
  let SoldOut;
  if (isLegacyStockProductData(props)) {
    if (
      props.outOfStock ||
      (props.salePeriod.endDate && new Date(props.salePeriod.endDate).getTime() < baseTimestamp)
    ) {
      SoldOut = {
        type: 'SoldOut' as const,
      };
    }
  } else {
    if (
      props.outOfStock ||
      props.stock.isOutOfStock ||
      (props.salePeriod.endDate && new Date(props.salePeriod.endDate).getTime() < baseTimestamp)
    ) {
      SoldOut = {
        type: 'SoldOut' as const,
      };
    }
  }

  let Upcoming;
  if (props.salePeriod.startDate) {
    const diff = ProductTimeHelper.diffDate(props.salePeriod.startDate, new Date(baseTimestamp));
    const hiddenCondition =
      diff.day < 0 || diff.hour < 0 || diff.minute < 0 || diff.second < 0 || diff.day >= 7;

    if (!hiddenCondition) {
      Upcoming = {
        type: 'Upcoming' as const,
        startDate: props.salePeriod.startDate,
      };
    }
  }

  let Trend;
  if (props.status?.isTrending) {
    Trend = {
      type: 'Trend' as const,
    };
  }

  let Popular;
  if (props.status?.isPopular) {
    Popular = {
      type: 'Popular' as const,
    };
  }

  let BrandDeal;
  if (props.status?.isBrandDeal) {
    BrandDeal = {
      type: 'BrandDeal' as const,
    };
  }

  let Membership;
  if (props.status?.isMembership) {
    Membership = {
      type: 'Membership' as const,
    };
  }

  let Nft;
  if (props.status?.isNft) {
    Nft = {
      type: 'Nft' as const,
    };
  }

  let Time_left;
  if (props.salePeriod.endDate) {
    const endDate = new Date(props.salePeriod.endDate);
    const hoursDiff = differenceInHours(endDate, new Date(baseTimestamp));
    const minutesDiff = differenceInMinutes(endDate, new Date(baseTimestamp));

    // 잔여시간이 168시간 미만일 경우에만 뱃지 표시
    if (hoursDiff < 168) {
      if (hoursDiff >= 96) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 8,
        };
      } else if (hoursDiff >= 48) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 7,
        };
      } else if (hoursDiff >= 24) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 6,
        };
      } else if (hoursDiff >= 12) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 5,
        };
      } else if (hoursDiff >= 1) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 4,
        };
      } else if (minutesDiff >= 10) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 3,
        };
      } else if (minutesDiff >= 1) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 2,
        };
      } else if (minutesDiff >= 0) {
        Time_left = {
          type: 'Time_left' as const,
          endDate,
          priority: 1,
        };
      }
    }
  }

  let Stock_left;
  if (isLegacyStockProductData(props)) {
    if (
      props.legacyStock.quantity > 0 &&
      isFinite(props.legacyStock.quantity) &&
      props.legacyStock.quantity > props.legacyStock.order_count &&
      !props.outOfStock &&
      props.legacyStock.quantity < 500 &&
      props.legacyStock.isStockPublic
    ) {
      const availableStock = props.legacyStock.quantity - props.legacyStock.order_count;

      if (availableStock < 10) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 3,
        };
      } else if (availableStock < 50) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 4,
        };
      } else if (availableStock < 100) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 5,
        };
      } else if (availableStock < 300) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 6,
        };
      } else if (availableStock < 500) {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 7,
        };
      } else {
        Stock_left = {
          type: 'Stock_left' as const,
          availableStock,
          priority: 8,
        };
      }
    }
  } else {
    // 재고가 threshold 미만이면 잔여 재고를 표시한다.
    if (props.lowStockMeta && props.stock.availableStock < props.lowStockMeta.threshold) {
      Stock_left = {
        type: 'Stock_left' as const,
        availableStock: props.stock.availableStock,
        priority: null,
      };
    }
  }

  let Limited;
  if (isLegacyStockProductData(props)) {
    if (Stock_left || Time_left) {
      Limited = {
        type: 'Limited' as const,
      };
    } else if (props.salePeriod.endDate) {
      Limited = {
        type: 'Limited' as const,
      };
    } else if (props.legacyStock.quantity > 0 && isFinite(props.legacyStock.quantity)) {
      Limited = {
        type: 'Limited' as const,
      };
    }
  } else {
    if (props.limitedMeta && props.limitedMeta.isLimited) {
      Limited = {
        type: 'Limited' as const,
      };
    }
  }

  let Funding;
  if (props.status.isFunding) {
    Funding = {
      type: 'Funding' as const,
    };
  }

  return {
    SoldOut,
    Upcoming,
    Trend,
    Popular,
    BrandDeal,
    Stock_left,
    Time_left,
    Limited,
    Membership,
    Nft,
    Funding,
  };
};

export const createBrandDealBadges = (
  props: BadgeParams,
  baseTimestamp: number,
): TProductBadgeViewProps[] => {
  const { Trend, Popular, BrandDeal, Limited, Upcoming } = createBadges(props, baseTimestamp);

  if (Trend && BrandDeal) {
    return [Trend, BrandDeal];
  }

  if (Popular && BrandDeal) {
    return [Popular, BrandDeal];
  }

  if (BrandDeal && Limited) {
    return [BrandDeal, Limited];
  }

  if (BrandDeal && Upcoming) {
    return [BrandDeal, Upcoming];
  }

  if (BrandDeal) {
    return [BrandDeal];
  }

  return [];
};

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=21842-59387&m=dev
 */
export const createProductListBadges = (
  props: BadgeParams,
  baseTimestamp: number,
): TProductBadgeViewProps[] => {
  const { SoldOut, Membership, Trend, Popular, Limited, Time_left, Stock_left, Nft, Upcoming } = createBadges(
    props,
    baseTimestamp,
  );

  /** Membership */
  if (Membership) {
    // Membership + Trend
    if (Membership && Trend) {
      return [Membership, Trend];
    }

    // Membership + Popular
    if (Membership && Popular) {
      return [Membership, Popular];
    }

    // Membership + Upcoming
    if (Membership && Upcoming) {
      return [Membership, Upcoming];
    }

    // Membership + Limited
    if (Membership && Limited) {
      return [Membership, Limited];
    }

    // Membership + Nft
    if (Membership && Nft) {
      return [Membership, Nft];
    }

    // Membership
    if (Membership) {
      return [Membership];
    }
  }

  /** Trend */
  if (Trend) {
    // Trend + Upcoming
    if (Trend && Upcoming) {
      return [Trend, Upcoming];
    }

    if (Trend && Stock_left && !SoldOut) {
      return [Trend, Stock_left];
    }

    // Trend + Nft + Time_left
    if (Trend && Time_left && !SoldOut) {
      return [Trend, Time_left];
    }

    // Trend + Limited
    if (Trend && Limited) {
      return [Trend, Limited];
    }

    // Trend + Nft
    if (Trend && Nft) {
      return [Trend, Nft];
    }

    // Trend
    if (Trend) {
      return [Trend];
    }
  }

  /** Popular */
  if (Popular) {
    // Popular + Upcoming
    if (Popular && Upcoming) {
      return [Popular, Upcoming];
    }

    // Popular + Limited
    if (Popular && Limited) {
      return [Popular, Limited];
    }

    if (Popular && Stock_left && !SoldOut) {
      return [Popular, Stock_left];
    }

    if (Popular && Time_left && !Stock_left) {
      return [Popular, Time_left];
    }

    // Popular + Nft
    if (Popular && Nft) {
      return [Popular, Nft];
    }

    // Popular
    if (Popular) {
      return [Popular];
    }
  }

  /** Limited */
  if (Limited) {
    if (Limited && Upcoming) {
      return [Upcoming];
    }

    if (Limited && !SoldOut && Stock_left && Time_left && !Stock_left.priority) {
      return [Limited, Stock_left, Time_left];
    }

    if (Limited && !SoldOut && Stock_left && Time_left && Stock_left.priority) {
      if (Stock_left.priority && Stock_left.priority <= Time_left.priority) {
        return [Limited, Stock_left];
      } else if (Stock_left.priority && Stock_left.priority > Time_left.priority) {
        return [Limited, Time_left];
      }
    }

    // Limited + Stock_left
    if (Limited && !SoldOut && Stock_left) {
      return [Limited, Stock_left];
    }

    // Limited + Time_left
    if (Limited && !SoldOut && Time_left) {
      return [Limited, Time_left];
    }

    // Limited + Nft
    if (Limited && Nft) {
      return [Limited, Nft];
    }

    // Limited
    if (Limited) {
      return [Limited];
    }
  }

  if (Upcoming) {
    return [Upcoming];
  }

  // Stock_left + Time_left
  if (!SoldOut && Stock_left && Time_left) {
    return [Stock_left, Time_left];
  }

  // Stock_left
  if (!SoldOut && Stock_left) {
    return [Stock_left];
  }

  // Time_left
  if (!SoldOut && Time_left) {
    return [Time_left];
  }

  return [];
};

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=21842-59613&m=dev
 */
export const createProductDetailBadges = (
  props: BadgeParams,
  baseTimestamp: number,
): TProductBadgeViewProps[] => {
  const { SoldOut, Membership, Trend, Popular, Limited, Time_left, Stock_left, Nft, Upcoming } = createBadges(
    props,
    baseTimestamp,
  );

  /** SoldOut */
  if (SoldOut) {
    // 품절 + 멤버십 + 급상승
    // SoldOut + Membership + Trend
    if (SoldOut && Membership && Trend) {
      return [SoldOut, Membership, Trend];
    }

    // 품절 + 멤버십 + 인기
    // SoldOut + Membership + Popular
    if (SoldOut && Membership && Popular) {
      return [SoldOut, Membership, Popular];
    }

    // 품절 + 멤버십 + 한정
    // SoldOut + Membership + Limited
    if (SoldOut && Membership && Limited) {
      return [SoldOut, Membership, Limited];
    }

    // 품절 + 멤버십 + NFT
    // SoldOut + Membership + Nft
    if (SoldOut && Membership && Nft) {
      return [SoldOut, Membership, Nft];
    }

    // 품절 + 멤버십
    // SoldOut + Membership
    if (SoldOut && Membership) {
      return [SoldOut, Membership];
    }

    // 품절 + 급상승 + 한정
    // SoldOut + Trend + Limited
    if (SoldOut && Trend && Limited) {
      return [SoldOut, Trend, Limited];
    }

    // 품절 + 급상승 + NFT
    // SoldOut + Trend + Nft
    if (SoldOut && Trend && Nft) {
      return [SoldOut, Trend, Nft];
    }

    // 품절 + 인기 + 한정
    // SoldOut + Popular + Limited
    if (SoldOut && Popular && Limited) {
      return [SoldOut, Popular, Limited];
    }

    // 품절 + 인기 + NFT
    // SoldOut + Popular + Nft
    if (SoldOut && Popular && Nft) {
      return [SoldOut, Popular, Nft];
    }

    // 품절 + 급상승
    // SoldOut + Trend
    if (SoldOut && Trend) {
      return [SoldOut, Trend];
    }

    // 품절 + 인기
    // SoldOut + Popular
    if (SoldOut && Popular) {
      return [SoldOut, Popular];
    }

    // 품절 + 한정 + NFT
    // SoldOut + Limited + Nft
    if (SoldOut && Limited && Nft) {
      return [SoldOut, Limited, Nft];
    }

    // 품절 + 한정
    // SoldOut + Limited
    if (SoldOut && Limited) {
      return [SoldOut, Limited];
    }

    // 품절 + NFT
    // SoldOut + Nft
    if (SoldOut && Nft) {
      return [SoldOut, Nft];
    }

    // 품절
    // SoldOut
    if (SoldOut) {
      return [SoldOut];
    }
  }

  /** Membership */
  if (Membership) {
    // 멤버십 + 급상승 + 한정
    // Membership + Trend + Limited
    if (Membership && Trend && Limited) {
      return [Membership, Trend, Limited];
    }

    // 멤버십 + 급상승 + NFT
    // Membership + Trend + Nft
    if (Membership && Trend && Nft) {
      return [Membership, Trend, Nft];
    }

    // 멤버십 + 급상승
    // Membership + Trend
    if (Membership && Trend) {
      return [Membership, Trend];
    }

    // 멤버십 + 인기 + 한정
    // Membership + Popular + Limited
    if (Membership && Popular && Limited) {
      return [Membership, Popular, Limited];
    }

    // 멤버십 + 인기 + NFT
    // Membership + Popular + Nft
    if (Membership && Popular && Nft) {
      return [Membership, Popular, Nft];
    }

    // 멤버십 + 인기
    // Membership + Popular
    if (Membership && Popular) {
      return [Membership, Popular];
    }

    // 멤버십 + 출시예정
    // Membership + Upcoming
    if (Membership && Upcoming) {
      return [Membership, Upcoming];
    }

    // 멤버십 + 한정 + 잔여개수
    // Membership + Limited + Stock_left
    if (Membership && Limited && Stock_left) {
      return [Membership, Limited, Stock_left];
    }

    // 멤버십 + 한정 + 잔여시간
    // Membership + Limited + Time_left
    if (Membership && Limited && Time_left) {
      return [Membership, Limited, Time_left];
    }

    // 멤버십 + 한정
    // Membership + Limited
    if (Membership && Limited) {
      return [Membership, Limited];
    }

    // 멤버십 + 출시예정
    // Membership + Upcoming
    if (Membership && Upcoming) {
      return [Membership, Upcoming];
    }

    // 멤버십 + Nft + 잔여개수
    // Membership + Nft + Stock_left
    if (Membership && Nft && Stock_left) {
      return [Membership, Nft, Stock_left];
    }

    // 멤버십 + Nft + 잔여시간
    // Membership + Nft + Time_left
    if (Membership && Nft && Time_left) {
      return [Membership, Nft, Time_left];
    }

    // 멤버십 + Nft
    // Membership + Nft
    if (Membership && Nft) {
      return [Membership, Nft];
    }

    if (Membership && Stock_left) {
      return [Membership, Stock_left];
    }

    if (Membership && Time_left) {
      return [Membership, Time_left];
    }

    // 멤버십
    // Membership
    if (Membership) {
      return [Membership];
    }
  }

  /** Trend */
  if (Trend) {
    // 급상숭 + 출시예정
    // Trend + Upcoming
    if (Trend && Upcoming) {
      return [Trend, Upcoming];
    }

    // 급상승 + 한정 + 잔여개수/잔여시간
    // Trend + Limited + Stock_left/Time_left
    if (Trend && Limited && Stock_left && Time_left && Stock_left.priority) {
      if (Stock_left.priority <= Time_left.priority) {
        return [Trend, Limited, Stock_left];
      } else {
        return [Trend, Limited, Time_left];
      }
    }

    // 급상승 + 한정 + 잔여시간
    // Trend + Limited + Time_left
    if (Trend && Limited && Time_left) {
      return [Trend, Limited, Time_left];
    }

    if (Trend && Stock_left && Time_left) {
      return [Trend, Stock_left, Time_left];
    }

    // 급상승 + NFT + 잔여개수
    // Trend + Nft + Stock_left
    if (Trend && Nft && Stock_left) {
      return [Trend, Nft, Stock_left];
    }

    // 급상승 + NFT + 잔여시간
    // Trend + Nft + Time_left
    if (Trend && Nft && Time_left) {
      return [Trend, Nft, Time_left];
    }

    // 급상승 + 한정
    // Trend + Limited
    if (Trend && Limited) {
      return [Trend, Limited];
    }

    if (Trend && Stock_left) {
      return [Trend, Stock_left];
    }
    if (Trend && Time_left) {
      return [Trend, Time_left];
    }

    // 급상승 + NFT
    // Trend + Nft
    if (Trend && Nft) {
      return [Trend, Nft];
    }

    // 급상승
    // Trend
    if (Trend) {
      return [Trend];
    }
  }

  /** Popular */
  if (Popular) {
    // Popular + Upcoming
    if (Popular && Upcoming) {
      return [Popular, Upcoming];
    }

    // 인기 + 한정 + 잔여개수/잔여시간 (우선순위 비교)
    // Popular + Limited + Stock_left/Time_left
    if (Popular && Limited && Stock_left && Time_left && Stock_left.priority) {
      if (Stock_left.priority <= Time_left.priority) {
        return [Popular, Limited, Stock_left];
      } else {
        return [Popular, Limited, Time_left];
      }
    }

    // 인기 + 한정 + 잔여개수
    // Popular + Limited + Stock_left
    if (Popular && Limited && Stock_left) {
      return [Popular, Limited, Stock_left];
    }

    // 인기 + 한정 + 잔여시간
    // Popular + Limited + Time_left
    if (Popular && Limited && Time_left) {
      return [Popular, Limited, Time_left];
    }

    // 인기 + NFT + 잔여개수
    // Popular + Nft + Stock_left
    if (Popular && Nft && Stock_left) {
      return [Popular, Nft, Stock_left];
    }

    // 인기 + NFT + 잔여시간
    // Popular + Nft + Time_left
    if (Popular && Nft && Time_left) {
      return [Popular, Nft, Time_left];
    }

    // 인기 + 한정
    // Popular + Limited
    if (Popular && Limited) {
      return [Popular, Limited];
    }

    if (Popular && Stock_left && Time_left) {
      return [Popular, Stock_left, Time_left];
    }
    if (Popular && Stock_left) {
      return [Popular, Stock_left];
    }
    if (Popular && Time_left) {
      return [Popular, Time_left];
    }

    // 인기 + NFT
    // Popular + Nft
    if (Popular && Nft) {
      return [Popular, Nft];
    }

    // 인기
    // Popular
    if (Popular) {
      return [Popular];
    }
  }

  if (Limited) {
    /** Limited */
    // 한정 + NFT + 출시예정
    // Limited + Nft + Upcoming
    if (Limited && Nft && Upcoming) {
      return [Limited, Nft, Upcoming];
    }

    if (Limited && Upcoming) {
      return [Upcoming];
    }

    // 한정 + NFT + 잔여개수/잔여시간 (우선순위 비교)
    // Limited + Stock_left/Time_left + Nft
    if (Limited && Nft && Stock_left && Time_left && Stock_left.priority) {
      if (Stock_left.priority <= Time_left.priority) {
        return [Limited, Stock_left, Nft];
      } else {
        return [Limited, Time_left, Nft];
      }
    }

    if (Limited && Stock_left && Time_left && !Stock_left.priority) {
      return [Limited, Stock_left, Time_left];
    }

    // 한정 + NFT + 잔여개수
    // Limited + Stock_left + Nft
    if (Limited && Stock_left && Nft) {
      return [Limited, Stock_left, Nft];
    }

    // 한정 + NFT + 잔여시간
    // Limited + Nft + Time_left
    if (Limited && Time_left && Nft) {
      return [Limited, Time_left, Nft];
    }

    // 한정 + NFT
    // Limited + Nft
    if (Limited && Nft) {
      return [Limited, Nft];
    }

    // 한정 + 잔여개수/잔여시간 (우선순위 비교)
    // Limited + Stock_left/Time_left
    if (Limited && Stock_left && Time_left && Stock_left.priority) {
      if (Stock_left.priority <= Time_left.priority) {
        return [Limited, Stock_left];
      } else {
        return [Limited, Time_left];
      }
    }

    // 한정 + 잔여개수
    // Limited + Stock_left
    if (Limited && Stock_left) {
      return [Limited, Stock_left];
    }

    // 한정 + 잔여시간
    // Limited + Time_left
    if (Limited && Time_left) {
      return [Limited, Time_left];
    }

    // 한정
    // Limited
    if (Limited) {
      return [Limited];
    }
  }

  /** Upcoming */
  if (Upcoming) {
    return [Upcoming];
  }

  // Stock_left + Time_left
  if (Stock_left && Time_left) {
    return [Stock_left, Time_left];
    // Stock_left
  } else if (Stock_left) {
    return [Stock_left];
    // Time_left
  } else if (Time_left) {
    return [Time_left];
  }

  return [];
};

/**
 * 펀딩 - 상품 상세 뱃지
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=22749-7634&m=dev
 *
 * @link
 * https://github.com/marpple/marpple-web/blob/4b1742286c6ce4be1409075c4d7b34243182c448/services/MarppleShop/renderApp/components/cells/ProductBadgeList/strategies/product-badge-strategy.funding.ts#L16-L198
 */
export const createFundingDetailBadges = (
  props: BadgeParams,
  baseTimestamp: number,
): TProductBadgeViewProps[] => {
  const { Funding, SoldOut, Membership, Time_left, Stock_left, Upcoming } = createBadges(
    props,
    baseTimestamp,
  );

  const Closed = {
    type: 'Closed' as const,
  };

  // Membership + Funding + Closed
  if (Membership && Funding && SoldOut) {
    return [Membership, Funding, Closed];
  }

  // Membership + Funding + Upcoming
  if (Membership && Funding && Upcoming) {
    return [Membership, Funding, Upcoming];
  }

  // Membership + Funding + Stock_left
  if (Membership && Funding && Stock_left) {
    return [Membership, Funding, Stock_left];
  }

  // Membership + Funding + Time_left
  if (Membership && Funding && Time_left) {
    return [Membership, Funding, Time_left];
  }

  // Membership + Funding
  if (Membership && Funding) {
    return [Membership, Funding];
  }

  // Funding + Closed
  if (Funding && SoldOut) {
    return [Funding, Closed];
  }

  // Funding + Upcoming
  if (Funding && Upcoming) {
    return [Funding, Upcoming];
  }

  // Funding + Stock_left
  if (Funding && Stock_left) {
    return [Funding, Stock_left];
  }

  // Funding + Time_left
  if (Funding && Time_left) {
    return [Funding, Time_left];
  }

  // Funding
  if (Funding) {
    return [Funding];
  }

  return [];
};

/**
 * 펀딩 - 상품 목록 뱃지
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=22749-14358&m=dev
 *
 * @link
 * https://github.com/marpple/marpple-web/blob/4b1742286c6ce4be1409075c4d7b34243182c448/services/MarppleShop/renderApp/components/cells/ProductBadgeList/strategies/product-badge-strategy.funding.ts#L16-L198
 */
export const createFundingListBadges = (
  props: BadgeParams,
  baseTimestamp: number,
): TProductBadgeViewProps[] => {
  const { Funding, SoldOut, Membership, Time_left, Stock_left, Upcoming } = createBadges(
    props,
    baseTimestamp,
  );

  const Closed = {
    type: 'Closed' as const,
  };

  // Membership + Funding + Closed
  if (Membership && Funding && SoldOut) {
    return [Membership, Funding, Closed];
  }

  // Membership + Funding + Upcoming
  if (Membership && Funding && Upcoming) {
    return [Membership, Funding, Upcoming];
  }

  // Membership + Funding + Stock_left
  if (Membership && Funding && Stock_left) {
    return [Membership, Funding, Stock_left];
  }

  // Membership + Funding + Time_left
  if (Membership && Funding && Time_left) {
    return [Membership, Funding, Time_left];
  }

  // Membership + Funding
  if (Membership && Funding) {
    return [Membership, Funding];
  }

  // Funding + Closed
  if (Funding && SoldOut) {
    return [Funding, Closed];
  }

  // Funding + Upcoming
  if (Funding && Upcoming) {
    return [Funding, Upcoming];
  }

  // Funding + Stock_left
  if (Funding && Stock_left) {
    return [Funding, Stock_left];
  }

  // Funding + Time_left
  if (Funding && Time_left) {
    return [Funding, Time_left];
  }

  // Funding
  if (Funding) {
    return [Funding];
  }

  return [];
};
